import { isPluginProduct } from '@common/api/productContext';
import type { WorkspacePlan } from '@common/api/authService';
import { getRouteBehavior } from '@/routeBehavior';

export type PaymentReturnResult = 'success' | 'cancel' | null;

export interface PaymentReturnParamFallback {
    workspaceId?: string | null;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: string | null;
}

export interface PaymentReturnParams {
    result: PaymentReturnResult;
    onboardingSuccess: boolean;
    workspaceId: string | null;
    planCode: string | null;
    planName: string | null;
    billingCycle: 'monthly' | 'yearly' | null;
    checked: boolean;
    requireActiveStatus?: boolean;
    onboardingTrialSetup?: boolean;
}

type VerifiedWorkspacePlan = Pick<
    WorkspacePlan,
    'status' | 'badge' | 'actionLabel' | 'trialEndAt'
>;

const hasFutureTrialEnd = (trialEndAt?: string | null): boolean => {
    if (!trialEndAt) return false;
    const timestamp = new Date(trialEndAt).getTime();
    return Number.isFinite(timestamp) && timestamp > Date.now();
};

const isWorkspacePlanTrial = (workspacePlan?: VerifiedWorkspacePlan | null): boolean => {
    const status = workspacePlan?.status?.trim().toLowerCase();
    const badge = workspacePlan?.badge?.trim().toLowerCase();
    const actionLabel = workspacePlan?.actionLabel?.trim().toLowerCase();
    return status === 'trial'
        || status === 'trialing'
        || badge === 'trial'
        || actionLabel === 'start subscription'
        || hasFutureTrialEnd(workspacePlan?.trialEndAt);
};

export const isWorkspacePlanPaid = (workspacePlan?: VerifiedWorkspacePlan | null): boolean => {
    const status = workspacePlan?.status?.trim().toLowerCase();
    return ['active', 'paid', 'pending_cancellation'].includes(status || '')
        && !isWorkspacePlanTrial(workspacePlan);
};

export const shouldRetryOnboardingPaymentConfirmation = (
    productCode: string | null | undefined,
    paymentReturn: Pick<PaymentReturnParams, 'onboardingSuccess' | 'onboardingTrialSetup'>,
    verifiedPlan?: VerifiedWorkspacePlan | null,
): boolean => {
    if (
        !paymentReturn.onboardingSuccess
        || paymentReturn.onboardingTrialSetup
        || isPluginProduct(productCode)
    ) {
        return false;
    }

    const status = verifiedPlan?.status?.trim().toLowerCase();
    return !status || ['pending', 'processing', 'incomplete', 'trial', 'trialing'].includes(status);
};

export const PAYMENT_RESULT_PARAMS_TO_CLEAR = [
    'pay_result',
    'payment_checked',
    'workspace_id',
    'workspaceId',
    'planCode',
    'planName',
    'billingCycle',
    'onboarding_trial_setup',
] as const;

const readReturnParamValue = (
    searchParams: URLSearchParams,
    key: string,
    fallback?: string | null,
): string | null => {
    const urlValue = searchParams.get(key)?.trim();
    if (urlValue) {
        return urlValue;
    }

    const fallbackValue = fallback?.trim();
    return fallbackValue || null;
};

const readBillingCycleParam = (
    searchParams: URLSearchParams,
    fallback?: string | null,
): 'monthly' | 'yearly' | null => {
    const value = readReturnParamValue(searchParams, 'billingCycle', fallback);
    if (value === 'monthly' || value === 'yearly') {
        return value;
    }

    return null;
};

export const readPaymentReturnParams = (
    searchParams: URLSearchParams,
    fallback: PaymentReturnParamFallback = {},
): PaymentReturnParams => {
    const rawResult = searchParams.get('pay_result');
    const onboardingSuccess = searchParams.get('onboarding_success') === 'true';
    const result = rawResult === 'success' || rawResult === 'cancel'
        ? rawResult
        : onboardingSuccess
            ? 'success'
            : null;

    return {
        result,
        onboardingSuccess,
        workspaceId: readReturnParamValue(searchParams, 'workspace_id', fallback.workspaceId),
        planCode: readReturnParamValue(searchParams, 'planCode', fallback.planCode),
        planName: readReturnParamValue(searchParams, 'planName', fallback.planName),
        billingCycle: readBillingCycleParam(searchParams, fallback.billingCycle),
        checked: searchParams.get('payment_checked') === 'true' || onboardingSuccess,
        onboardingTrialSetup: searchParams.get('onboarding_trial_setup') === 'true',
    };
};

/**
 * Onboarding creates a workspace-specific success state. Setup/trial returns
 * should show that welcome state instead of layering a billing success modal
 * over it. A real paid Core onboarding checkout remains a payment success.
 */
export const shouldShowProductPaymentSuccessModal = (
    productCode: string | null | undefined,
    paymentReturn: Pick<PaymentReturnParams, 'onboardingSuccess' | 'onboardingTrialSetup'>,
    pathname: string,
    verifiedPlan?: VerifiedWorkspacePlan | null,
): boolean => {
    if (getRouteBehavior(pathname).pageKind === 'onboarding') {
        return false;
    }

    if (!paymentReturn.onboardingSuccess) {
        return true;
    }

    return !paymentReturn.onboardingTrialSetup
        && !isPluginProduct(productCode)
        && isWorkspacePlanPaid(verifiedPlan);
};

/**
 * Plugin Admin treats a successful payment return as a one-shot event. Consume
 * its URL params at the same moment the success modal opens so a browser
 * refresh cannot replay the modal. Core keeps its existing return behavior.
 */
export const shouldConsumeProductPaymentSuccessParamsOnOpen = (
    productCode: string | null | undefined,
    paymentReturn: Pick<PaymentReturnParams, 'onboardingSuccess' | 'onboardingTrialSetup'>,
    pathname: string,
): boolean => {
    return isPluginProduct(productCode)
        && shouldShowProductPaymentSuccessModal(productCode, paymentReturn, pathname);
};

export const shouldClearPayResultImmediately = (
    pathname: string,
    payResult: string | null,
): boolean => {
    if (payResult === 'success' && getRouteBehavior(pathname).pageKind === 'onboarding') {
        return false;
    }

    return true;
};

export const shouldFinalizeOnboardingPaymentSuccess = (
    pathname: string,
    payResult: string | null,
    workspaceId?: string | null,
): boolean => {
    return getRouteBehavior(pathname).pageKind === 'onboarding'
        && payResult === 'success'
        && Boolean(workspaceId);
};

export const buildOnboardingPaymentSuccessParams = (
    workspaceId: string,
    paymentReturn?: Pick<PaymentReturnParams, 'planCode' | 'planName' | 'billingCycle'>,
): Record<string, string> => {
    const params: Record<string, string> = {
        onboarding_success: 'true',
        workspace_id: workspaceId,
    };

    const planCode = paymentReturn?.planCode?.trim();
    const planName = paymentReturn?.planName?.trim();
    if (planCode) {
        params.planCode = planCode;
    }
    if (planName) {
        params.planName = planName;
    }
    if (paymentReturn?.billingCycle) {
        params.billingCycle = paymentReturn.billingCycle;
    }

    return params;
};

/**
 * The checkout URL is only a handoff hint. Once Admin has refreshed the
 * workspace, the verified plan must drive the success presentation so an
 * onboarding URL without plan params cannot fall back to Solo.
 */
export const resolvePaymentSuccessPlan = (
    paymentReturn: Pick<PaymentReturnParams, 'planCode' | 'planName'>,
    verifiedPlan?: Pick<PaymentReturnParamFallback, 'planCode' | 'planName'> | null,
): Pick<PaymentReturnParamFallback, 'planCode' | 'planName'> => ({
    planCode: verifiedPlan?.planCode?.trim() || paymentReturn.planCode,
    planName: verifiedPlan?.planName?.trim() || paymentReturn.planName,
});

export const shouldSwitchWorkspaceForPaymentResult = (
    payResult: string | null,
    requestedWorkspaceId?: string | null,
    currentWorkspaceId?: string | null,
): boolean => {
    return payResult === 'success'
        && Boolean(requestedWorkspaceId)
        && requestedWorkspaceId !== currentWorkspaceId;
};

export const resolvePaymentWorkspaceAction = (
    payResult: string | null,
    requestedWorkspaceId?: string | null,
    currentWorkspaceId?: string | null,
    isLoadingWorkspaces: boolean = false,
): 'wait' | 'continue' => {
    if (payResult !== 'success' || !requestedWorkspaceId) {
        return 'continue';
    }

    if (isLoadingWorkspaces || !currentWorkspaceId) {
        return 'wait';
    }

    if (requestedWorkspaceId !== currentWorkspaceId) {
        return 'wait';
    }

    return 'continue';
};
